import React, {useState, FormEvent} from 'react';
import {useNavigate} from 'react-router-dom';
import {Image} from 'mui-image';

import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import {userApi} from '../../api/openapi-axios-client';
import Copyright from '../Copyright';
import {landingImage} from '../../assets';
import { callApi } from '../../api/helpers';
import { MessageResponse, ResetPasswordFormData } from '../../types';
const ResetPassword: React.FC = () => {
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    setError('');
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const email = formData.get('email') as string;

    if (!email) {
      setError('No Email entered');
      return;
    }

    const submissionsData: ResetPasswordFormData = {
      email
    };

    try {
      const data : MessageResponse = await callApi<MessageResponse>(() => userApi.password.requestReset({...submissionsData}));
      
      if (data?.message) {
        navigate('/');
      } else {
        setError('error requesting password reset');
      }
    } catch (err) {
      setError('error requesting reset');
    }
  };

  return (
    <Container
      maxWidth={isMobile ? false : "lg"}
      disableGutters={isMobile}
      sx={{
        px: { xs: 2, sm: 3, md: 3 },
        py: { xs: 2, sm: 3, md: 4 },
        width: '100%'
      }}
    >
      <Grid container direction='row' justifyContent="center" spacing={isMobile ? 2 : 5} sx={{mt: isMobile ? 4 : 10, px: isMobile ? 2 : 0}}>
        <Grid size={{xs: 12, md: 6}} display={{xs: 'none', md: 'block'}}>
          <Box component='img' src={landingImage} alt="Future Gen reset password illustration" sx={{width: '100%', maxWidth: '500px'}} />
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'center' : 'flex-start',
            maxWidth: isMobile ? '100%' : '400px',
            width: '100%',
            mx: isMobile ? 0 : 'auto'
          }}>
            {error && <Alert severity='error' sx={{width: '100%', mb: 2}}>{error}</Alert>}

            <Typography component='h5' variant='h5' sx={{
              fontWeight: 900,
              textAlign: isMobile ? 'center' : 'left',
              fontSize: isMobile ? '1.5rem' : '1.75rem'
            }}>
              Reset Your Password
            </Typography>
            <Typography component='p' variant='body1' sx={{
              textAlign: isMobile ? 'center' : 'left',
              mt: 1,
              mb: 2
            }}>
              Submit your email and if your email is in our system we will send you a reset link.
            </Typography>
            <Box component='form' onSubmit={handleSubmit} noValidate sx={{mt: 1, width: '100%'}}>
              <TextField
                margin='normal'
                required
                fullWidth
                id='email'
                label='Email'
                name='email'
                autoComplete='email'
                autoFocus
                size={isMobile ? 'small' : 'medium'}
              />
              <Button
                type='submit'
                fullWidth
                variant='contained'
                sx={{mt: 3, mb: 2, height: isMobile ? '48px' : '56px'}}
              >
                Request Password Reset
              </Button>
              <Grid container sx={{justifyContent: 'space-evenly', margin: 1}}>
                <Grid>
                  <Link
                    component='button'
                    variant='body1'
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      event.preventDefault();
                      navigate('/login');
                    }}
                    sx={{fontSize: isMobile ? '0.875rem' : '1rem'}}
                  >
                    Sign In
                  </Link>
                </Grid>
                <Grid>
                  <Link
                    component='button'
                    variant='body1'
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      event.preventDefault();
                      navigate('/register');
                    }}
                    sx={{fontSize: isMobile ? '0.875rem' : '1rem'}}
                  >
                    Sign Up
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>

        <Box sx={{width: '100%', mt: 4}}>
          <Copyright/>
        </Box>
      </Grid>
    </Container>
  );
};

export default ResetPassword; 